import { ReactElement } from 'react';
import { useParams, useLocation, Navigate } from 'react-router-dom';
import styles from './Venue.module.scss';
import VenueDetails from 'src/components/VenueDetails';
import VenueCategories from 'src/components/VenueCategories';
import VenueChannels from 'src/components/VenueChannels';
import VenueContacts from 'src/components/VenueContacts';
import VenueOpeningTimesContents from 'src/components/VenueOpeningTimeContents/VenueOpeningTimesContents';
import { useVenue } from 'src/hooks/useVenue';
import { useExternalSystems } from 'src/hooks/useExternalSystems';
import {
  BreadcrumbHeading,
  DetailsPanel,
  Tab,
  TabContent,
  TabList,
  TabsProvider,
  VenueIcon,
  Spinner,
} from '@weareopenr/component-library';
import { Routes } from 'src/routes';
import { SpinnerContainer } from 'src/components/SpinnerContainer';

const Venue = (): ReactElement => {
  const { pathname } = useLocation();
  const { venueId } = useParams<{ venueId: string }>();
  const { data: venue, isLoading: isLoadingVenue } = useVenue(venueId, pathname);
  const { data: externalSystems, isFetching: isFetchingExternalSystems } = useExternalSystems();

  if (isLoadingVenue || isFetchingExternalSystems) {
    return (
      <SpinnerContainer data-testid="loadingIcon">
        <Spinner size="xlarge" colour="rainbow" />
      </SpinnerContainer>
    );
  }

  if (!venue || !externalSystems) return <Navigate to={Routes.VENUES} replace />;

  const validChannels = venue.channels.filter((channel) => !(channel.name === 'Just Eat' && !channel.externalId));

  return (
    <>
      <BreadcrumbHeading
        breadcrumbs={[
          { label: 'Venues', path: '/venues' },
          { label: venue.name, path: `/venues/${venue.venueId}` },
        ]}
      />

      <TabsProvider defaultTab="venue-details">
        <TabList className={styles['tab-list']}>
          <Tab id="venue-details" tabText="Venue Details" />
          <Tab id="venue-channels" tabText="Channels" />
          <Tab id="venue-contacts" tabText="Contacts" />
          <Tab id="venue-opening-times" tabText="Opening Times" />
        </TabList>
        <div className={styles['container']}>
          <TabContent id="venue-details">
            <div className={styles['two-row-container']}>
              <div className={styles['two-column-container']}>
                <VenueDetails venue={venue} />
                <DetailsPanel title="Image">
                  {venue.imageUrl ? (
                    <img className={styles['item-image']} src={venue.imageUrl} alt={venue.name} />
                  ) : (
                    <div className={styles['image-placeholder']}>
                      <VenueIcon size="xlarge" />
                    </div>
                  )}
                </DetailsPanel>
              </div>
              <VenueCategories categories={venue.categories || []} />
            </div>
          </TabContent>
          <TabContent id="venue-channels">
            <VenueChannels venue={venue} channels={validChannels} aggregators={externalSystems} />
          </TabContent>
          <TabContent id="venue-contacts">
            <VenueContacts venue={venue} />
          </TabContent>
          <TabContent id="venue-opening-times">
            <VenueOpeningTimesContents venue={venue} />
          </TabContent>
        </div>
      </TabsProvider>
    </>
  );
};

export default Venue;
